// Shared chrome for the Client Portal pages after sign-in (Session AFH Jobber-Client-Hub redesign).
// SEVEN top-level sections, one tap from anywhere: a persistent LEFT SIDEBAR on desktop (>= 768px),
// a fixed BOTTOM TAB BAR on mobile. Mobile-first, branded, plain English (the customer touches this,
// so assume zero lift and zero code knowledge). A 3px tri-colour accent bar tops every page.
//
// The nav chrome renders only when the shell is used as a SIGNED-IN app page (signalled by
// onSignOut, which every authenticated page passes). The error fallbacks render the plain wrapper
// (no nav) exactly as before.
import type { ReactNode, ComponentType } from "react";
import { Link, NavLink } from "react-router-dom";
import { House, Wrench, AlertCircle, FileText, Building2, GraduationCap, Hammer } from "lucide-react";
import { WhatsAppLink } from "../../components/WhatsAppLink";
import { PrivacyNoticeLink } from "../../components/PrivacyNoticeLink";
import { SKYLINE_WATERMARK_COLOUR, SKYLINE_WATERMARK_OPACITY, SKYLINE_WATERMARK_HEIGHT } from "../../lib/skyline";

const PHONE = "0203 576 4760";
// The committed brand logo (public/brand/, Session ACE; the convention for every
// public-facing page; never recoloured).
const LOGO_SRC = "/brand/ele-logo.png";

interface NavItem {
  to: string;
  label: string;
  // Session AMX: the label the MOBILE tab bar uses, when the full one will not fit a seventh tab.
  // The desktop sidebar is a vertical list with room to spare and always shows the full `label`,
  // so shortening for the phone never costs the desktop reader clarity.
  shortLabel?: string;
  icon: ComponentType<{ size?: number; color?: string }>;
  end?: boolean;
}

// The SEVEN top-level sections, in order. Still no "More" tab: every section is reachable in one tap.
//
// This was FIVE (Session AFH), then SIX (Session AGJ, so Training is reachable without opening a
// site first). Session AMX makes it SEVEN, on the Director's decision, so Repairs and Works has its
// own place instead of being reachable only by opening a site first.
//
// SIX WAS DOCUMENTED HERE AS THE CEILING, and it genuinely was for the bar as it was then built.
// Measured at 375px in Chromium with the real self-hosted font: "Documents" held ~70px on its
// min-content floor and the other five absorbed the difference at ~61px each, giving a ~53px content
// box against "CALL-OUTS" at ~54px natural. That wrapped, and so did "Service History" (~86px), so
// the SHIPPED six-item bar already carried two wrapped labels. The note said a seventh needed "a
// real rethink (shorter labels, grouping, or a genuine overflow), not another append". This is that
// rethink, and it was re-measured the same way rather than argued.
//
// WHAT THE MEASUREMENT SHOWED. A naive seventh append is far worse than it looks: "REPAIRS AND
// WORKS" is ~102px natural against a ~41px box, wrapping to THREE lines and taking the bar from
// 69px to 81px tall. And no amount of shortening fixes "CALL-OUTS" on its own, because the browser
// breaks it AT THE HYPHEN: at seven equal tabs it wrapped to "CALL-" / "OUTS" in every label set
// tried, including the shortest sensible one.
//
// SO THE FIX IS TWO PARTS, and the second is the one that actually mattered:
//   1. Mobile-only short labels (`shortLabel` above) for the three longest: Service History ->
//      SERVICE, Documents -> DOCS, Repairs and Works -> REPAIRS. The full words stay on desktop.
//   2. `whitespace-nowrap` on the tab label (see BottomTab). This is what keeps "Call-outs"
//      hyphenated and correct: the house term is "Call-outs", so the WORD was never the thing to
//      change. Denying the break makes each tab's min-content its whole label, so flexbox
//      distributes by real width instead of breaking mid-word, and px-0.5 gives back the 28px
//      seven tabs need.
//
// MEASURED RESULT at 320 / 360 / 375 / 390px, self-hosted Roboto 400 at 10px uppercase: no wrap,
// no clipping and no horizontal overflow at any of them, bar height 69px. That is the SAME height
// as the shipped six-item bar with two fewer wrapped labels, so the seventh section leaves the
// phone nav better than it found it. A future EIGHTH still needs its own rethink: at 320px this
// fits with nothing to spare.
//
// Training sits after Documents and before Sites: the information sections first, then the places.
// Repairs and Works sits next to Call-outs, since both are reactive work.
// Overview matches /portal EXACTLY (end); Sites matches /portal/sites and /portal/sites/:id.
const NAV_ITEMS: NavItem[] = [
  { to: "/portal", label: "Overview", icon: House, end: true },
  { to: "/portal/history", label: "Service History", shortLabel: "Service", icon: Wrench },
  { to: "/portal/callouts", label: "Call-outs", icon: AlertCircle },
  // Hammer, not Wrench: Service History already owns the wrench, and two identical glyphs on one
  // tab bar read as one section at 20px.
  { to: "/portal/repairs", label: "Repairs and Works", shortLabel: "Repairs", icon: Hammer },
  { to: "/portal/documents", label: "Documents", shortLabel: "Docs", icon: FileText },
  { to: "/portal/training", label: "Training", icon: GraduationCap },
  { to: "/portal/sites", label: "Sites", icon: Building2 },
];

export function PortalShell({
  children,
  wide,
  userName,
  onSignOut,
}: {
  children: ReactNode;
  wide?: boolean;
  userName?: string;
  onSignOut?: () => void;
}) {
  const container = wide ? "max-w-3xl" : "max-w-md";
  const nav = Boolean(onSignOut);

  // Plain wrapper (error fallbacks): the accent bar, the brand header and the
  // footer, with no nav chrome. Unchanged from the pre-AFH shell.
  if (!nav) {
    return (
      <div className="flex min-h-dvh flex-col bg-ele-bg text-ele-body">
        <div aria-hidden className="ele-portal-accent" />
        <header className="border-b border-white/10 bg-ele-bg">
          <div className={`mx-auto flex w-full ${container} items-center justify-between gap-3 px-4 py-3`}>
            <div className="flex items-center gap-3">
              <Link
                to="/portal"
                aria-label="Go to your portal home"
                className="-my-1.5 flex min-h-11 flex-none items-center rounded-ele outline-none focus-visible:ring-2 focus-visible:ring-ele-purple"
              >
                <img src={LOGO_SRC} alt="East London Elevators" className="h-8 w-auto" />
              </Link>
              <span className="font-heading text-base tracking-wide text-ele-white">Client Portal</span>
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 py-8">
          <div className={`mx-auto w-full ${container}`}>
            {children}
            <PortalFooter />
          </div>
        </main>
      </div>
    );
  }

  // Signed-in app chrome: sidebar on desktop, bottom tab bar on mobile.
  return (
    <div className="portal-ui portal-light flex min-h-dvh flex-col bg-white text-ele-body">
      <div aria-hidden className="ele-portal-accent" />
      <PortalBrandBand onSignOut={onSignOut!} />
      <div className="flex flex-1">
        <PortalSidebar userName={userName} onSignOut={onSignOut!} />
        <div className="flex min-w-0 flex-1 flex-col">
          {/* pb-24 keeps the last of the content clear of the fixed mobile bottom bar; the
              sidebar has no such bar, so desktop restores the normal bottom padding. */}
          <main className="flex-1 px-4 py-8 pb-24 md:pb-10">
            <div className={`ele-fade-in mx-auto w-full ${container}`}>
              {children}
              <PortalFooter light />
            </div>
          </main>
        </div>
      </div>
      <PortalBottomBar />
    </div>
  );
}

// Dark brand band across the top of every signed-in page, the same header the quote page carries:
// the logo and "Client Portal" on the left, the phone and email on the right, sign out on a phone.
export function PortalBrandBand({
  onSignOut,
  label = "Client Portal",
  home = "/portal",
  signOutAlways = false,
}: {
  onSignOut: () => void;
  label?: string;
  home?: string;
  signOutAlways?: boolean;
}) {
  return (
    <header
      className="px-4 py-4 md:px-8"
      style={{ background: "linear-gradient(90deg, #050816 0%, #1a0a33 55%, #2b0f4f 100%)" }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Link
            to={home}
            aria-label="Go to your portal home"
            className="flex min-h-11 w-fit items-center rounded-ele outline-none focus-visible:ring-2 focus-visible:ring-ele-purple"
          >
            <img src={LOGO_SRC} alt="East London Elevators" className="h-9 w-auto" />
          </Link>
          <span className="font-menu text-[11px] font-bold uppercase tracking-[0.2em] text-[#63C41F]">{label}</span>
        </div>
        <div className="flex flex-col items-end gap-1 text-right font-body text-xs text-[#CACACA]">
          <a href="tel:02035764760" className="text-sm font-bold text-[#2CB9F3]">{PHONE}</a>
          <a href="mailto:info@eastlondonelevators.co.uk" className="hidden text-[#CACACA] sm:inline">info@eastlondonelevators.co.uk</a>
          <button
            type="button"
            onClick={onSignOut}
            className={`font-menu text-sm tracking-wide text-[#2CB9F3] transition hover:underline ${signOutAlways ? "" : "md:hidden"}`}
          >
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

// Desktop-only left sidebar (>= 768px): the sections and sign out. Light, like the page. Active =
// purple wash + purple text; hover = a quieter wash. NO left stripe (the no-accent-stripe rule).
function PortalSidebar({ userName, onSignOut }: { userName?: string; onSignOut: () => void }) {
  return (
    <aside className="hidden w-60 flex-none flex-col border-r border-[#e5e7eb] bg-white md:flex">
      <nav aria-label="Portal sections" className="flex flex-1 flex-col gap-1 px-3 pt-5">
        {NAV_ITEMS.map((item) => (
          <SidebarLink key={item.to} item={item} />
        ))}
      </nav>
      <div className="px-3 pb-6 pt-3">
        {userName && (
          <p className="mb-2 px-2 font-body text-xs text-[#6b6b6b]">
            {userName}
          </p>
        )}
        <button
          type="button"
          onClick={onSignOut}
          className="w-full rounded-ele px-2 py-2 text-left font-menu text-xs tracking-wide text-[#0e74a6] transition hover:underline"
        >
          Sign out
        </button>
      </div>
    </aside>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  const { icon: Icon, label, to, end } = item;
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-ele px-3 py-2.5 font-body text-sm transition ${
          isActive ? "bg-[#7d21c7]/10 text-[#7d21c7]" : "text-[#4b5563] hover:bg-[#f7f7f9]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={18} color={isActive ? "#7d21c7" : "#6b6b6b"} />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}

// Mobile-only fixed bottom tab bar (< 768px): seven thumb-reachable destinations. Active = purple
// icon + label + a soft wash, no underline/edge bar (the app-wide no-accent-stripe rule).
function PortalBottomBar() {
  return (
    <nav
      aria-label="Portal sections"
      className="fixed inset-x-0 bottom-0 z-40 flex border-t border-[#e5e7eb] bg-white md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {NAV_ITEMS.map((item) => (
        <BottomTab key={item.to} item={item} />
      ))}
    </nav>
  );
}

function BottomTab({ item }: { item: NavItem }) {
  const { icon: Icon, label, shortLabel, to, end } = item;
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 px-0.5 py-1.5 text-center font-menu text-[10px] uppercase tracking-wide transition ${
          isActive ? "bg-[#7d21c7]/10" : ""
        }`
      }
    >
      {({ isActive }) => {
        const colour = isActive ? "#7d21c7" : "#6b6b6b";
        return (
          <>
            <Icon size={20} color={colour} />
            {/* whitespace-nowrap is load-bearing, not cosmetic: without it the browser breaks
                "CALL-OUTS" at its hyphen and the seventh tab wraps the bar onto two lines. See
                the measurement note above NAV_ITEMS. */}
            <span className="whitespace-nowrap" style={{ color: colour }}>
              {shortLabel ?? label}
            </span>
          </>
        );
      }}
    </NavLink>
  );
}

// Shared footer (company legal copy + contact). Exported so the standalone login landing page
// reuses the exact same footer. Session ACE: the London skyline strip sits between the purple
// rule and the legal copy (the email-chrome footer pattern). Session ACG raised its opacity
// 0.45 -> 0.55 so the strip would hold its weight against the tighter layout.
//
// Session AFJ SUPERSEDES the ACG opacity and the raw-artwork render behind it: the strip was
// effectively invisible on the dark portal, and no opacity could have fixed it (the line art's
// own colour is a near-black purple, so it converges on the background). It is now a near-white
// CSS-mask watermark, the Session AFG pattern already proven on the engineer PWA home. The full
// reasoning and the measured figures live with the values in lib/skyline.ts.
// The RFID page remains the one surface that drops the skyline entirely (its one-screen
// exception, not a precedent).
export function PortalFooter({ light = false }: { light?: boolean } = {}) {
  return (
    <footer className={`mt-10 border-t pt-4 text-center text-xs ${light ? "border-[#e5e7eb] text-[#4b5563]" : "border-white/10 text-ele-body"}`}>
      {/* A mask fills the element's BACKGROUND, not an <img>'s pixels, so this is a div and the
          <picture> webp negotiation goes with it. It masks the 1200 PNG rather than the master
          (a third of the weight, the same 3:1 artwork this footer already loaded). */}
      <div
        aria-hidden="true"
        data-ele-skyline=""
        className="w-full"
        style={{
          height: light ? "120px" : SKYLINE_WATERMARK_HEIGHT,
          // light: the quote page's own ink and weight (--cq-skyline-ink #29104b at 0.7), larger
          backgroundColor: light ? "#29104b" : SKYLINE_WATERMARK_COLOUR,
          opacity: light ? 0.7 : SKYLINE_WATERMARK_OPACITY,
          WebkitMaskImage: "url('/brand/london-skyline-1200.png')",
          maskImage: "url('/brand/london-skyline-1200.png')",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          // Bottom-anchored, mirroring the object-bottom this replaces.
          WebkitMaskPosition: "bottom",
          maskPosition: "bottom",
          // contain, never "100% auto": the artwork is exactly 3:1, so scaling it to the footer
          // width would make it 256px tall inside an 80px box and crop the buildings away.
          WebkitMaskSize: "contain",
          maskSize: "contain",
        }}
      />
      <div className={light ? "mt-3" : "mt-3 opacity-60"}>
        <p>For and on behalf of East London Elevators.</p>
        <p className="mt-1">East London Elevators Ltd, company 15429089, VAT GB493339461</p>
        <p className="mt-1">{PHONE} | info@eastlondonelevators.co.uk</p>
        <p className="mt-1">
          <WhatsAppLink color={light ? "#2f6d16" : undefined} />
        </p>
      </div>
      {/* Session ASQ (Stream G, Item 5): shown only once the privacy notice is published, and outside the dimmed block
          above so the link keeps its full contrast on this dark canvas. Every portal page carries this footer. */}
      <PrivacyNoticeLink className="mt-2" color={light ? "#0e74a6" : undefined} />
      {/* The quote page's dark footer bar (registered address only). */}
      {light && (
        <div className="mt-5 rounded-xl bg-[#050816] px-4 py-4 text-center text-xs leading-6 text-[#CACACA]">
          <p>East London Elevators Ltd &middot; Company No. 15429089 &middot; VAT GB493339461</p>
          <p>Unit 82a James Carter Road, Mildenhall, Bury St Edmunds, IP28 7DE</p>
          <p>
            <b className="text-[#2CB9F3]">{PHONE}</b> &middot;{" "}
            <a href="mailto:info@eastlondonelevators.co.uk" className="text-[#2CB9F3]">info@eastlondonelevators.co.uk</a> &middot;{" "}
            <a href="https://eastlondonelevators.co.uk" className="text-[#2CB9F3]">eastlondonelevators.co.uk</a>
          </p>
        </div>
      )}
    </footer>
  );
}
